import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import {
  addLocalePrefix,
  persistLocalePreference,
  stripLocalePrefix,
} from "@/lib/locale";

/**
 * `EN / \u4e2d` toggle for the sticky nav. Renders both labels with a hairline
 * separator. Active label is in --frac-violet; inactive in --text-tertiary.
 *
 * Clicking the inactive label fires the loading-screen sequence via
 * `onTransitionStart` (provided by the LanguageTransitionController). The
 * controller handles the visual, then completes the navigation.
 *
 * Props:
 *   onTransitionStart({ target, path })  — required; controller callback.
 *   className                            — optional utility overrides.
 *   variant                              — "on-light" (nav) | "on-dark" (mobile drawer)
 */
export function LanguageToggle({
  onTransitionStart,
  className,
  variant = "on-light",
  testIdPrefix = "lang-toggle",
}) {
  const { t } = useT("nav");
  const location = useLocation();
  const navigate = useNavigate();
  const current = useLocale();

  const trigger = useCallback(
    (target) => {
      if (target === current) return;
      const canonical = stripLocalePrefix(location.pathname);
      const path =
        addLocalePrefix(canonical, target) +
        (location.search || "") +
        (location.hash || "");
      persistLocalePreference(target);
      if (onTransitionStart) {
        onTransitionStart({ target, path });
      } else {
        // Fallback (no controller wired) — just navigate.
        navigate(path);
      }
    },
    [current, location, navigate, onTransitionStart],
  );

  const idle =
    variant === "on-light"
      ? "text-frac-purple/45 hover:text-frac-violet"
      : "text-ink-tertiary hover:text-ink-primary";
  const active =
    variant === "on-light"
      ? "text-frac-violet"
      : "text-frac-violet";
  const separator =
    variant === "on-light"
      ? "bg-frac-purple/20"
      : "bg-hairline";

  const btnCls = "inline-flex items-center px-1 text-[12px] font-semibold uppercase tracking-caps transition-colors duration-fast ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frac-violet rounded-sm";

  return (
    <div
      className={cn("inline-flex items-center gap-1", className)}
      role="group"
      aria-label="Language"
      data-testid={testIdPrefix}
    >
      <button
        type="button"
        onClick={() => trigger("en")}
        aria-pressed={current === "en"}
        aria-label={t("languageToggle.ariaSwitchTo", { lang: "English" })}
        className={cn(btnCls, current === "en" ? active : idle)}
        data-testid={`${testIdPrefix}-en`}
      >
        {t("languageToggle.en")}
      </button>
      <span
        aria-hidden
        className={cn("h-3 w-px", separator)}
      />
      <button
        type="button"
        onClick={() => trigger("zh")}
        aria-pressed={current === "zh"}
        aria-label={t("languageToggle.ariaSwitchTo", { lang: "\u4e2d\u6587" })}
        className={cn(btnCls, "text-[14px]", current === "zh" ? active : idle)}
        data-testid={`${testIdPrefix}-zh`}
      >
        {t("languageToggle.zh")}
      </button>
    </div>
  );
}

export default LanguageToggle;
